/**
 * 斜めのカードの画像（再スキャンの候補。Issue #932）。サーバーが画像の枠の線から傾きを測る（AI は使わない。`card_scan_check.py`）。
 * 「傾きを測る」で測っていない画像を全部測る（PDF の取り込みでは切り出したその場で測る）。一覧は傾きの大きい順で、
 * 登録済みはカード名・型番・レアリティつき。再スキャンするカードの一覧はテキストでコピーできる。
 * 再スキャンして登録したら、古い画像はここで消す（使っているデッキは残った画像へ付け替わる）。
 * 画像の一覧はふだん畳んでおき、見出しを押して開く。
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/api';
import { ImageRow } from './CardImageManager';
import ZoomableImage from './ZoomableImage';

const KIND = 'card_scan_check';
const THRESHOLDS = [0.3, 0.5, 1, 2];
const POLL_MS = 3000;
const PAGE = 30;
const LANG_LABEL = { jp: '日本語', en: 'English' };

const tiltText = (t) => `${Math.abs(t).toFixed(1)}°`;

export function rescanList(rows) {
  return rows.map((r) => {
    const i = r.image;
    if (!i) return `（登録前）${r.s3_key}\t${tiltText(r.tilt)}`;
    return [i.card_name ?? '不明', i.card_id, LANG_LABEL[i.language_code] ?? i.language_code, i.set_code ?? '型番なし',
      i.rarity ?? '', `#${String(i.image_index).padStart(2, '0')}`, tiltText(r.tilt)].join('\t');
  }).join('\n');
}

export default function TiltedCardImages({ refreshKey }) {
  const [minTilt, setMinTilt] = useState(0.5);
  const [rows, setRows] = useState([]);
  const [targets, setTargets] = useState(null);
  const [targetsError, setTargetsError] = useState(null);   // 数が取れない（サーバーのデプロイ中など）
  const [job, setJob] = useState(null);
  const [shown, setShown] = useState(PAGE);
  const [open, setOpen] = useState(false);                 // 一覧は畳んでおく（画像が多く、ふだんは見ない）

  const load = useCallback(() => api.project('GetTiltedCardImages', { min_tilt: minTilt })
    .then((r) => setRows(Array.isArray(r) ? r : []))
    .catch(() => setRows([])), [minTilt]);
  const loadTargets = useCallback(() => api.project('GetCardScanCheckTargets')
    .then((r) => { setTargets(r?.count ?? null); setTargetsError(null); })
    .catch((err) => { setTargets(null); setTargetsError(err.message); }), []);
  const loadJob = useCallback(async () => {
    try {
      const r = await api.project('GetExportRequests', { kind: KIND });
      setJob(r?.[0] ?? null);
      return r?.[0] ?? null;
    } catch { return null; }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { loadTargets(); loadJob(); }, [loadTargets, loadJob, refreshKey]);

  const running = job?.status === 'processing' || job?.status === 'pending';
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(async () => {
      const now = await loadJob();
      if (now && now.status !== 'processing' && now.status !== 'pending') {
        load();
        loadTargets();
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [running, loadJob, load, loadTargets]);

  const start = async () => {
    try {
      await api.project('CreateCardScanCheckRequest', {});
      await loadJob();
    } catch (err) { alert('始められませんでした: ' + err.message); }
  };
  const removeTemp = async (key) => {
    if (!window.confirm('この登録前の画像を消しますか？')) return;
    try {
      await api.project('DeleteTempCardImage', { s3_key: key });
      load();
    } catch (err) { alert('消せませんでした: ' + err.message); }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(rescanList(rows));
      alert(`${rows.length} 件をコピーしました`);
    } catch { alert('コピーできませんでした'); }
  };

  return (
    <div style={{ marginTop: 32 }} data-testid="tilted-card-images">
      <h2 style={{ fontSize: '1rem', marginBottom: 4, color: '#aaa', cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen((v) => !v)} data-testid="tilt-toggle">
        {open ? '▼' : '▶'} 斜めの画像（再スキャンの候補） ({rows.length}件)
      </h2>
      <div className="card-meta" style={{ marginBottom: 8 }}>
        画像の枠の線から傾きを測っています。再スキャンして登録したら、古い画像は「削除」してください（使っているデッキは残った画像へ付け替わります）。
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <select className="form-select" style={{ width: 120 }} value={minTilt} data-testid="tilt-threshold"
          onChange={(e) => { setMinTilt(Number(e.target.value)); setShown(PAGE); }}>
          {THRESHOLDS.map((t) => <option key={t} value={t}>{t}° 以上</option>)}
        </select>
        {/* 押せないのは測っている最中と、測るものが 0 枚のときだけ。数が取れなくても押せる（理由は横に出す） */}
        <button className="btn btn-secondary btn-sm" disabled={running || targets === 0} onClick={start} data-testid="tilt-start">
          {targets === 0 ? '傾きはすべて測り済み' : `傾きを測る（まだの画像 ${targets ?? '?'} 枚）`}
        </button>
        {targetsError && (
          <span className="card-meta" style={{ color: '#e94560' }} data-testid="tilt-targets-error">
            まだの画像の数が取れません（{targetsError}）。サーバーの更新中なら少し待って再読み込みしてください
          </span>
        )}
        <button className="btn btn-secondary btn-sm" disabled={rows.length === 0} onClick={copy}>再スキャンする一覧をコピー</button>
        {job && (
          <span className="card-meta" data-testid="tilt-job">
            {running ? '測定中' : { completed: '測定済み', failed: '失敗', cancelled: '止めた' }[job.status] ?? job.status}
            {job.progress && `: ${job.progress}`}
            {job.error_message && `（${job.error_message}）`}
          </span>
        )}
      </div>
      {open && rows.length === 0 && <p className="loading">ありません</p>}
      {open && <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.slice(0, shown).map((r) => (r.image ? (
          <ImageRow key={r.s3_key} img={r.image} allowLanguage onChanged={load}
            heading={<><strong>{r.image.card_name ?? '不明'}（{r.image.card_id}）</strong> — 傾き {tiltText(r.tilt)}</>} />
        ) : (
          <div key={r.s3_key} className="card" style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <ZoomableImage src={r.download_url} alt={r.s3_key} />
            <div style={{ flex: 1 }}>
              <div className="card-meta" style={{ marginBottom: 8 }}>登録前 {r.s3_key} — 傾き {tiltText(r.tilt)}</div>
              <button className="btn btn-danger btn-sm" onClick={() => removeTemp(r.s3_key)}>削除</button>
            </div>
          </div>
        )))}
      </div>}
      {open && rows.length > shown && (
        <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={() => setShown((n) => n + PAGE)}>
          もっと見る（残り {rows.length - shown} 件）
        </button>
      )}
    </div>
  );
}
